import { useEffect, useRef, type MutableRefObject } from 'react';
import { getPerformanceMark } from '.';
import { useHeightObserver } from './useHeightObserver';
import { usePerformanceAnalytics } from './usePerformanceAnalytics';
import { PERFORMANCE_ANALYTICS_INTERVAL } from './PerformanceAnalyticsProvider';

import type { ViewportDimensions } from '../renderer/forge-ui-renderer/types';

interface Props {
	isEnabled: boolean;
	viewportDimensions?: ViewportDimensions;
}

export const useResizeAnalytics = ({
	isEnabled,
	viewportDimensions,
}: Props): {
	containerRef: MutableRefObject<HTMLDivElement | null>;
} => {
	const { captureEvent } = usePerformanceAnalytics();
	const { containerRef, height } = useHeightObserver();
	const isTrackingRef = useRef(true);

	// Initialize tracking timeout on mount, clear on unmount
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			isTrackingRef.current = false;
		}, PERFORMANCE_ANALYTICS_INTERVAL);

		return () => clearTimeout(timeoutId);
	}, []);

	// Capture resize events when height changes and tracking is active
	useEffect(() => {
		// Only capture if height is valid, feature is enabled, and we're still tracking
		if (height !== null && isEnabled && isTrackingRef.current) {
			captureEvent({
				type: 'resized',
				height,
				measuredHeight: viewportDimensions?.measuredHeight,
				viewportHeight: viewportDimensions?.viewportHeight,
				elapsed: getPerformanceMark(),
			});
		}
	}, [height, isEnabled, captureEvent, viewportDimensions]);

	return { containerRef };
};
